import React, { useState } from "react";
import { Phone, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Overline, Reveal } from "../components/common";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CONTACT } from "../data/content";

export default function Contact() {
  const { t } = useTranslation();
  const INFO = [
    { icon: Phone, label: t("contact.info.call"),  value: CONTACT.phone, href: CONTACT.phoneHref,           testId: "contact-info-phone" },
    { icon: Mail,  label: t("contact.info.email"), value: CONTACT.email, href: `mailto:${CONTACT.email}`,   testId: "contact-info-email" },
  ];
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    reason: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // Formats a North American phone number as the user types: (123) 456-7890
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        reason: form.reason,
        message: form.message,
      };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success(t("contact.toast.successTitle"), {
        description: t("contact.toast.successBody"),
      });
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        reason: "",
        message: "",
      });
    } catch (err) {
      toast.error(t("contact.toast.errorTitle"), {
        description: t("contact.toast.errorBody"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-2 sm:pb-8 bg-gradient-to-b from-brand-mist/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t("contact.hero.overline")}</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05] max-w-4xl">
              {t("contact.hero.titleLead")} <span className="text-brand-teal">{t("contact.hero.titleAccent")}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-2xl">
              {t("contact.hero.subtitle")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-4 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Info */}
            <div className="order-2 lg:order-none lg:col-span-5 lg:col-start-8 lg:row-start-1">
              <Reveal>
                <div className="flex flex-col gap-5">
                  {INFO.map((item) => {
                    const IconCmp = item.icon;
                    const inner = (
                      <div className="flex items-center gap-4">
                        <div className="h-11 w-11 shrink-0 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal">
                          <IconCmp className="h-5 w-5" strokeWidth={1.7} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-wider text-brand-teal">
                            {item.label}
                          </p>
                          <p className="mt-0.5 text-brand-navy font-medium leading-snug whitespace-nowrap">
                            {item.value}
                          </p>
                        </div>
                      </div>
                    );
                    return item.href ? (
                      <a
                        key={item.testId}
                        href={item.href}
                        data-testid={item.testId}
                        className="block bg-brand-offwhite rounded-2xl border border-gray-100 p-6 hover:shadow-md transition-shadow"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div
                        key={item.testId}
                        data-testid={item.testId}
                        className="bg-brand-offwhite rounded-2xl border border-gray-100 p-6"
                      >
                        {inner}
                      </div>
                    );
                  })}
                </div>
              </Reveal>
            </div>

            {/* Form */}
            <div className="order-1 lg:order-none lg:col-span-7 lg:col-start-1 lg:row-start-1">
              <Reveal delay={0.1}>
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-10"
                >
                  <h2 className="text-2xl font-bold font-heading text-brand-navy">
                    {t("contact.form.title")}
                  </h2>
                  <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-brand-navy">
                        {t("contact.form.firstName")}
                      </Label>
                      <Input
                        id="firstName"
                        data-testid="contact-input-first-name"
                        value={form.firstName}
                        onChange={update("firstName")}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-brand-navy">
                        {t("contact.form.lastName")}
                      </Label>
                      <Input
                        id="lastName"
                        data-testid="contact-input-last-name"
                        value={form.lastName}
                        onChange={update("lastName")}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-brand-navy">
                        {t("contact.form.email")}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={update("email")}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-brand-navy">
                        {t("contact.form.phone")}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            phone: formatPhone(e.target.value),
                          }))
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="mt-5 space-y-2">
                    <Label className="text-brand-navy">{t("contact.form.reasonLabel")}</Label>
                    <Select
                      value={form.reason}
                      onValueChange={(v) => setForm((f) => ({ ...f, reason: v }))}
                    >
                      <SelectTrigger data-testid="contact-select-reason">
                        <SelectValue placeholder={t("contact.form.reasonPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="care">{t("contact.form.reasonCare")}</SelectItem>
                        <SelectItem value="support">{t("contact.form.reasonSupport")}</SelectItem>
                        <SelectItem value="partnership">{t("contact.form.reasonPartnership")}</SelectItem>
                        <SelectItem value="other">{t("contact.form.reasonOther")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-5 space-y-2">
                    <Label htmlFor="message" className="text-brand-navy">
                      {t("contact.form.message")}
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder={t("contact.form.messagePlaceholder")}
                      rows={5}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? t("contact.form.submitting") : t("contact.form.submit")}
                    {!submitting && <Send className="h-4 w-4" />}
                  </button>
                  <p className="mt-4 text-xs text-slate-400">
                    {t("contact.form.responseNote")}
                  </p>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
